import React from 'react';

const shimmer: React.CSSProperties = {
  background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
  backgroundSize: '200% 100%',
  animation: 'leShimmer 2s infinite',
};

const bar = (width: number | string, height: number, extra: React.CSSProperties = {}): React.CSSProperties => ({
  width,
  height,
  borderRadius: 6,
  flexShrink: 0,
  ...shimmer,
  ...extra,
});

const Sep = () => <div style={{ width: 1, height: 18, background: '#e5e7eb', margin: '0 4px', flexShrink: 0 }} />;

/** Skeleton for the Generate Letter editor: toolbar · left tools · A4 page · properties panel. */
const LetterEditorSkeletonLoader: React.FC = () => {
  return (
    <>
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#f0f4f8',
          overflow: 'hidden',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 12px',
            background: 'white',
            borderBottom: '1px solid #e5e7eb',
            minHeight: 46,
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <div style={bar(110, 16)} />
          <Sep />
          <div style={bar(170, 27)} />
          <Sep />
          {[28, 28, 28, 70, 70, 70].map((w, i) => (
            <div key={`a${i}`} style={bar(w, 27)} />
          ))}
          <Sep />
          {[60, 28, 28, 28, 28, 28].map((w, i) => (
            <div key={`b${i}`} style={bar(w, 27)} />
          ))}
          <Sep />
          {[28, 28, 28, 28].map((w, i) => (
            <div key={`c${i}`} style={bar(w, 27)} />
          ))}
          <div style={{ flex: 1 }} />
          <div style={bar(90, 30, { borderRadius: 8 })} />
          <div style={bar(90, 30, { borderRadius: 8 })} />
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left panel */}
          <div
            style={{
              width: 190,
              flexShrink: 0,
              background: 'white',
              borderRight: '1px solid #e5e7eb',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              overflow: 'hidden',
            }}
          >
            <div style={bar(70, 10, { marginBottom: 2 })} />
            {[...Array(6)].map((_, i) => (
              <div key={`l1${i}`} style={bar('100%', 30, { borderRadius: 8 })} />
            ))}
            <div style={bar(90, 10, { marginTop: 10, marginBottom: 2 })} />
            {[...Array(4)].map((_, i) => (
              <div key={`l2${i}`} style={bar('100%', 30, { borderRadius: 8 })} />
            ))}
          </div>

          {/* Canvas with A4 page */}
          <div
            style={{
              flex: 1,
              padding: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: 794,
                maxWidth: '100%',
                aspectRatio: '794 / 1123',
                flexShrink: 0,
                background: 'white',
                boxShadow: '0 4px 32px rgba(30, 58, 138, 0.15)',
                overflow: 'hidden',
              }}
            >
              {/* Header image */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '17%', ...shimmer }} />

              {/* S. No. (left) and Date (right) */}
              <div style={{ position: 'absolute', top: '25.5%', left: '13.5%', ...bar('34%', 12) }} />
              <div style={{ position: 'absolute', top: '23.5%', left: '73%', ...bar('16%', 12) }} />

              {/* Body text lines */}
              {[88, 94, 90, 96, 70, 0, 92, 84, 60].map((w, i) =>
                w === 0 ? null : (
                  <div
                    key={i}
                    style={{ position: 'absolute', left: '12%', top: `${34 + i * 3.2}%`, ...bar(`${w * 0.76}%`, 11) }}
                  />
                )
              )}

              {/* QR slot */}
              <div style={{ position: 'absolute', left: '6.3%', top: '76.5%', ...bar('12.6%', 0, { aspectRatio: '1 / 1', height: 'auto', borderRadius: 4 }) }} />

              {/* Footer image */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '10%', ...shimmer }} />
            </div>
          </div>

          {/* Properties panel */}
          <div
            style={{
              width: 200,
              flexShrink: 0,
              background: 'white',
              borderLeft: '1px solid #e5e7eb',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              overflow: 'hidden',
            }}
          >
            {[...Array(3)].map((_, g) => (
              <div key={g} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                <div style={bar(80, 10, { marginBottom: 2 })} />
                {[...Array(3)].map((_, i) => (
                  <div key={i}>
                    <div style={bar(50, 9, { marginBottom: 5 })} />
                    <div style={bar('100%', 28, { borderRadius: 8 })} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes leShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </>
  );
};

export default LetterEditorSkeletonLoader;
